import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks'; // Adjust path
import { fetchCustomerDetails } from '../../../store/slices/usersSlice'; // Adjust path
import { ArrowLeft, User, Phone, Mail, MapPin, Calendar, Package, CheckCircle, AlertCircle, FileText, CreditCard, Users, Image as ImageIcon } from 'lucide-react';
import Loader from '../../common/Loader'; // Adjust path
// Reuse CSS or create new? Reuse logic but not modal overlay.
// I will use inline classes or a new CSS file.

const CustomerDetailsPage: React.FC = () => {
    const { mobile } = useParams<{ mobile: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { customerDetails, customerDetailsLoading, customerDetailsError } = useAppSelector((state) => state.users);

    useEffect(() => {
        if (mobile) {
            dispatch(fetchCustomerDetails(mobile));
        }

        return () => {
            // Optional: clear details on unmount if we want fresh fetch next time
            // dispatch(clearCustomerDetails());
        };
    }, [dispatch, mobile]);

    if (customerDetailsLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <Loader />
            </div>
        );
    }

    if (customerDetailsError) {
        return (
            <div className="p-8 text-center">
                <div className="text-red-500 mb-4">Error: {customerDetailsError}</div>
                <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-200 rounded">Go Back</button>
            </div>
        );
    }

    if (!customerDetails || !customerDetails.user) {
        return null; // Or some empty state
    }

    const { user, stats, orders, referrals } = customerDetails;

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header / Back Button */}
            <div className="mb-6 flex items-center gap-4">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                >
                    <ArrowLeft size={24} />
                </button>
                <h1 className="text-2xl font-bold text-gray-800">Customer Details</h1>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">
                {/* User Profile Card */}
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                        <div className="flex-shrink-0">
                            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center text-3xl font-bold text-gray-500">
                                {user.first_name ? user.first_name[0].toUpperCase() : 'U'}
                            </div>
                        </div>
                        <div className="flex-grow">
                            <div className="flex flex-wrap gap-2 items-center mb-2">
                                <h2 className="text-2xl font-bold">{user.first_name} {user.last_name}</h2>
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${user.verified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {user.verified ? 'Verified' : 'Pending'}
                                </span>
                                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                    {user.role}
                                </span>
                                {stats.total_referrals > 0 && (
                                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 flex items-center gap-1">
                                        <Users size={12} /> {stats.total_referrals} Referrals
                                    </span>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mt-4">
                                <div className="flex items-center gap-2">
                                    <Phone size={16} /> {user.mobile}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Mail size={16} /> {user.email || 'N/A'}
                                </div>
                                {(user.city || user.state || user.pincode) && (
                                    <div className="flex items-center gap-2">
                                        <MapPin size={16} />
                                        {[user.city, user.state].filter(Boolean).join(', ')}
                                        {user.pincode ? ` - ${user.pincode}` : ''}
                                    </div>
                                )}
                                {user.user_created_date && (
                                    <div className="flex items-center gap-2">
                                        <Calendar size={16} /> Joined: {new Date(user.user_created_date).toLocaleDateString()}
                                    </div>
                                )}
                                {user.aadhar_number && (
                                    <div className="flex items-center gap-2">
                                        <FileText size={16} /> Aadhar: {user.aadhar_number}
                                    </div>
                                )}
                                {user.pan_number && (
                                    <div className="flex items-center gap-2">
                                        <CreditCard size={16} /> PAN: {user.pan_number}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Documents Section */}
                {(user.aadhar_front_image_url || user.aadhar_back_image_url || user.panCardUrl) && (
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <ImageIcon size={20} /> Documents
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {user.aadhar_front_image_url && (
                                <div className="border rounded p-2">
                                    <div className="text-sm font-medium mb-2 text-gray-500">Aadhar Front</div>
                                    <img src={user.aadhar_front_image_url} alt="Aadhar Front" className="w-full h-48 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity" onClick={() => window.open(user.aadhar_front_image_url, '_blank')} />
                                </div>
                            )}
                            {user.aadhar_back_image_url && (
                                <div className="border rounded p-2">
                                    <div className="text-sm font-medium mb-2 text-gray-500">Aadhar Back</div>
                                    <img src={user.aadhar_back_image_url} alt="Aadhar Back" className="w-full h-48 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity" onClick={() => window.open(user.aadhar_back_image_url, '_blank')} />
                                </div>
                            )}
                            {user.panCardUrl && (
                                <div className="border rounded p-2">
                                    <div className="text-sm font-medium mb-2 text-gray-500">PAN Card</div>
                                    <img src={user.panCardUrl} alt="PAN Card" className="w-full h-48 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity" onClick={() => window.open(user.panCardUrl, '_blank')} />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow text-center">
                        <div className="text-gray-500 text-xs uppercase mb-1">Total Orders</div>
                        <div className="text-2xl font-bold">{stats.total_orders}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow text-center">
                        <div className="text-gray-500 text-xs uppercase mb-1">Referrals</div>
                        <div className="text-2xl font-bold">{stats.total_referrals}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow text-center">
                        <div className="text-gray-500 text-xs uppercase mb-1">Total Coins</div>
                        <div className="text-2xl font-bold text-blue-600">{user.coins || 0}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow text-center">
                        <div className="text-gray-500 text-xs uppercase mb-1">Purchased</div>
                        <div className="text-2xl font-bold text-green-600">{stats.purchased_orders}</div>
                    </div>
                </div>



                {/* Orders Section */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Package size={20} /> Recent Orders
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3">Order ID</th>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3 text-center">Units</th>
                                    <th className="px-4 py-3 text-center">Buffalo/Calf</th>
                                    <th className="px-4 py-3 text-center">Coins</th>
                                    <th className="px-4 py-3">Amount</th>
                                    <th className="px-4 py-3">Order Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.length === 0 ? (
                                    <tr><td colSpan={7} className="px-4 py-4 text-center">No orders found</td></tr>
                                ) : (
                                    orders.map((order: any) => (
                                        <tr key={order.id} className="border-b hover:bg-gray-50">
                                            <td className="px-4 py-3 font-medium text-gray-900">{order.id}</td>
                                            <td className="px-4 py-3 whitespace-nowrap">{new Date(order.placedAt).toLocaleDateString()}</td>
                                            <td className="px-4 py-3 text-center">{order.numUnits}</td>
                                            <td className="px-4 py-3 text-center">{order.buffaloCount} / {order.calfCount}</td>
                                            <td className="px-4 py-3 text-center font-medium text-blue-600">
                                                {order.coinsRedeemed ? order.coinsRedeemed : '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">₹{order.totalCost?.toLocaleString()}</td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${order.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                                    order.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                                        'bg-blue-100 text-blue-800'
                                                    }`}>
                                                    {order.status || 'PENDING'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Referrals Section */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <User size={20} /> Referrals
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3">Name</th>
                                    <th className="px-4 py-3">Mobile</th>
                                    <th className="px-4 py-3">City</th>
                                    <th className="px-4 py-3 text-center">Total Coins</th>
                                    <th className="px-4 py-3 text-center">Verified</th>
                                </tr>
                            </thead>
                            <tbody>
                                {referrals.length === 0 ? (
                                    <tr><td colSpan={5} className="px-4 py-4 text-center">No referrals found</td></tr>
                                ) : (
                                    referrals.map((ref: any) => (
                                        <tr key={ref.id} className="border-b hover:bg-gray-50">
                                            <td className="px-4 py-3 font-medium text-gray-900">{ref.first_name} {ref.last_name}</td>
                                            <td className="px-4 py-3">{ref.mobile}</td>
                                            <td className="px-4 py-3">{ref.city}</td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="font-semibold text-blue-600">{ref.coins || 0}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {ref.verified ? <CheckCircle size={16} className="text-green-500" /> : <AlertCircle size={16} className="text-yellow-500" />}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerDetailsPage;
